import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import axios from "axios";
import { spacing } from "@mui/system";
import useSetting from "../../../../hooks/useSetting";

import SaveCancel from "../../../../components/SaveCancel";
import TabPanel from "../../../../components/TabPanel";
import { a11yProps } from "../../../../utils/common";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import MailSetting from "./components/MailSetting";
import Localization from "./components/Localization";
import SmsApi from "./components/SmsApi";
import Common from "./components/Common";
import InvoiceSettings from "./components/InvoiceSettings";
import BankDetails from "./components/BankDetails";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const validationSchema = Yup.object().shape({
  businessType: Yup.string().required("Required"),
  isEmailEnabled: Yup.boolean(),
  isSmsEnabled: Yup.boolean(),
  isParentalEnabled: Yup.boolean(),
  isRestrictRenewal: Yup.boolean(),
  mailFrom: Yup.string().when("isEmailEnabled", {
    is: true,
    then: (schema) => schema.required("Mail from is required"),
    //use arrow function to resolve branch is not defined error
    otherwise: (schema) => schema.notRequired(),
  }),

  hostName: Yup.string().when("isEmailEnabled", {
    is: true,
    //use arrow function to resolve branch is not defined error
    then: (schema) => schema.required("Hostname is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mailUserName: Yup.string().when("isEmailEnabled", {
    is: true,
    //use arrow function to resolve branch is not defined error
    then: (schema) => schema.required("Mail username is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  smsUserName: Yup.string().when("isSmsEnabled", {
    is: true,
    //use arrow function to resolve branch is not defined error
    then: (schema) => schema.required("Sms username is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  smsProvider: Yup.mixed().when("isSmsEnabled", {
    is: true,
    then: () =>
      Yup.object().shape({
        label: Yup.string().required("Label is required"),
        value: Yup.string().required("Value is required"),
      }),
    otherwise: () => Yup.mixed().notRequired().nullable(),
  }),
  country: Yup.object().shape({
    label: Yup.string().required("Is required"),
    value: Yup.string().required("Is required"),
  }),
  timeZone: Yup.string().required("Required"),
  mailPassword: Yup.string()
    .nullable()
    .when("isEmailEnabled", {
      is: true,
      //use arrow function to resolve branch is not defined error
      then: (schema) => schema.required("Mail password is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  smsPassword: Yup.string()
    .nullable()
    .when("isSmsEnabled", {
      is: true,
      //use arrow function to resolve branch is not defined error
      then: (schema) => schema.required("Sms password is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  port: Yup.string().when("isEmailEnabled", {
    is: true,
    //use arrow function to resolve branch is not defined error
    then: (schema) => schema.required("Port is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  invoiceTemplate: Yup.string().required("Required"),
  defaultExpiryTime: Yup.string().required("Required"),
  minimumDepositAmount: Yup.number()
    .nullable()
    .when("allowOnlineDeposit", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Minimum Deposit must be a number")
          .required("Minimum Deposit is required")
          .min(0, "Minimum Deposit cannot be negative"),
      otherwise: (schema) => schema.notRequired(),
    }),

  invoiceSettings: Yup.array().when("businessType", {
    is: "MULTI_SERVICE_OPERATOR",
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          invoicePrefix: Yup.string().required("Required"),
          lastInvoiceNumber: Yup.string().required("Required"),
          gstNumber: Yup.string().required("Required"),
          doorNo: Yup.string().required("Required"),
          pinCode: Yup.object()
            .shape({
              label: Yup.string().required(),
              value: Yup.string().required(),
            })
            .nullable()
            .required("Pincode is required"),
          area: Yup.string().required("Required"),
          city: Yup.string().required("Required"),
          state: Yup.string().required("Required"),
          country: Yup.string().required("Required"),
        }),
      ),
  }),
});

function Settings() {
  const { state } = useLocation();
  const [payload, setPayload] = useState(currentPayload);

  console.log(state, "statestatestate");
  const dispatch = useDispatch();

  const { getTenantSetting, tenantSetting, updateTenantSetting, isSubmitting } = useSetting();

  React.useEffect(() => {
    if (!tenantSetting || !tenantSetting.tenantInvoiceCounters) return;

    const fetchPostOfficeData = async () => {
      const updatedCounters = [...tenantSetting.tenantInvoiceCounters];

      await Promise.all(
        updatedCounters.map(async (counter, index) => {
          if (!counter.pinCode?.label) return;

          try {
            const res = await axios.get(
              `https://api.postalpincode.in/pincode/${counter.pinCode.label}`,
            );

            if (res.data[0]?.Status === "Success") {
              updatedCounters[index] = {
                ...counter,
                postOffices: res.data[0].PostOffice || [],
              };
            }
          } catch (err) {}
        }),
      );

      const initialValues = {
        tenantSettingId: tenantSetting.tenantSettingId,
        mailFrom: tenantSetting.mailFrom ?? "",
        country: {
          label: tenantSetting.country.countryName,
          value: tenantSetting.country.countryId,
        },
        mailPassword: tenantSetting.mailPassword,
        smsPassword: tenantSetting.smsPassword,
        smsProvider: tenantSetting.smsProvider
          ? {
              label: tenantSetting.smsProvider.smsProviderName,
              value: tenantSetting.smsProvider.smsProviderId,
            }
          : null,
        mobileNo: tenantSetting.mobileNo,
        email: tenantSetting.email,
        mailUserName: tenantSetting.mailUserName,
        isEmailEnabled: tenantSetting.isEmailEnabled,
        isSmsEnabled: tenantSetting.isSmsEnabled,
        isParentalEnabled: tenantSetting.isParentalEnabled,
        isSslEnabled: tenantSetting.isSslEnabled,
        isTlsEnabled: tenantSetting.isTlsEnabled,
        port: tenantSetting.port,
        smsUserName: tenantSetting.smsUserName,
        timeZone: tenantSetting.timeZone,
        hostName: tenantSetting.hostName,
        invoiceTemplate: tenantSetting.invoiceTemplate,
        defaultExpiryTime: tenantSetting.defaultExpiryTime,
        defaultExpiry: tenantSetting.defaultExpiry,
        allowOnlineDeposit: tenantSetting.allowOnlineDeposit,
        invoiceSettings: updatedCounters.length
          ? updatedCounters
          : [
              {
                invoicePrefix: "",
                lastInvoiceNumber: "0",
                tenantInvoiceCounterId: null,
                gstNumber: "",
                doorNo: "",
                pinCode: null,
                area: "",
                city: "",
                state: "",
                country: "",
                postOffices: [],
              },
            ],
        bankAccountName: tenantSetting?.bankAccountName,
        bankAccountNumber: tenantSetting?.bankAccountNumber,
        bankIfscCode: tenantSetting?.bankIfscCode,
        bankBranch: tenantSetting?.bankBranch,
        pan: tenantSetting?.pan,
        hsnCode: tenantSetting?.hsnCode,
        businessType: tenantSetting.businessType,
        agrSharing: tenantSetting.agr,
        isAgrEnabled: tenantSetting.isAgrEnabled,
        minimumDepositAmount: tenantSetting.minimumDepositAmount,
        defaultInvoiceStatus: tenantSetting.defaultInvoiceStatus,
        cafPrefix: tenantSetting.cafPrefix,
        isMultipleGst: tenantSetting.isMultipleGst,
        isImmediateRenewal: tenantSetting.isImmediateRenewal,
        numberOfHoldLimit:
          tenantSetting.numberOfHoldLimit == null ? 2 : tenantSetting.numberOfHoldLimit,
        isRestrictRenewal: tenantSetting.isRestrictRenewal,
      };

      reset(initialValues);
    };

    fetchPostOfficeData();
  }, [tenantSetting]);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });
  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [status] = useState({
    sent: false,
  });

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: {},
    watch,
  } = methods;

  const businessType = watch("businessType");

  const onSubmit = async (data) => {
    try {
      data["country"] = {
        countryId: data.country.value,
        countryName: data.country.label,
      };
      if (data["smsProvider"] != null && data["smsProvider"] != undefined) {
        data["smsProvider"] = {
          smsProviderId: data.smsProvider.value,
          smsProviderName: data.smsProvider.label,
        };
      }
      data["tenantInvoiceCounters"] = [];
      console.log(data["invoiceSettings"]);
      for (let i = 0; i < data["invoiceSettings"].length; i++) {
        if (data["invoiceSettings"][i]["invoicePrefix"] != "") {
          data["tenantInvoiceCounters"].push({
            tenantId: tenantSetting["tenant"]["tenantId"],
            invoicePrefix: data["invoiceSettings"][i]["invoicePrefix"],
            lastInvoiceNumber: data["invoiceSettings"][i]["lastInvoiceNumber"],
            tenantInvoiceCounterId: data["invoiceSettings"][i]["tenantInvoiceCounterId"],
            gstNumber: data["invoiceSettings"][i]["gstNumber"],
            doorNo: data["invoiceSettings"][i]["doorNo"],
            pinCode: data["invoiceSettings"][i]["pinCode"]["label"],
            area: data["invoiceSettings"][i]["area"],
            city: data["invoiceSettings"][i]["city"],
            state: data["invoiceSettings"][i]["state"],
            country: data["invoiceSettings"][i]["country"],
          });
        }
      }
      console.log(data, "data2333333");
      await updateTenantSetting(data);

      dispatch(showSuccessSnackbar("General Settings added successfully!"));
    } catch (err) {
      console.log(err, "ddddddddddddd");
      dispatch(showErrorSnackbar(err.message));
    }
  };

  const cancelBtn = () => {
    navigate("/cpanel/tenant/list");
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>cpanel</Typography>
        <Typography>tenant</Typography>
        <Typography>genaral setting</Typography>
      </Breadcrumbs>
    );
  };

  const fetchData = async () => {
    await getTenantSetting(state.id);
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("general_settings")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="Notification">
                    <Tab label="Common" {...a11yProps(0)} />
                    <Tab label="Mail Setting" {...a11yProps(1)} />
                    <Tab label="Localization" {...a11yProps(2)} />
                    <Tab label="Sms Api" {...a11yProps(3)} />
                    {businessType && businessType == "MULTI_SERVICE_OPERATOR" && (
                      <Tab label="Invoice & GST Settings" {...a11yProps(4)} />
                    )}
                    {businessType && businessType == "MULTI_SERVICE_OPERATOR" && (
                      <Tab label="Bank Details" {...a11yProps(5)} />
                    )}
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <Common validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <MailSetting validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={2}>
                    <Localization validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={3}>
                    <SmsApi validationFields={validationSchema.fields} />
                  </TabPanel>
                  {businessType && businessType == "MULTI_SERVICE_OPERATOR" && (
                    <TabPanel value={tabValue} index={4}>
                      <InvoiceSettings validationFields={validationSchema.fields} />
                    </TabPanel>
                  )}
                  {businessType && businessType == "MULTI_SERVICE_OPERATOR" && (
                    <TabPanel value={tabValue} index={5}>
                      <BankDetails validationFields={validationSchema.fields} />
                    </TabPanel>
                  )}
                </Box>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default Settings;
